import React, { useState, FC, useMemo } from 'react'
import { Link } from '@src/components/Link'
import { paperDetailRoute } from '@src/routes'
import { makeStyles } from '@material-ui/core/styles'
import {
  Link as MUILink,
  ClickAwayListener,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { usePaperDetailQuery } from '@src/queries/PaperDetailQuery.generated'

const useStyles = makeStyles((theme: Theme) => ({
  citation: {
    fontSize: '75%',
    position: 'relative',
    top: '-0.4em',
    paddingLeft: 1,
    cursor: 'pointer',
    unicodeBidi: 'isolate', // prevents wrapping in between text and citation
  },
  tooltipArrow: {
    background: theme.palette.common.white,
    boxShadow: '0 4px 6px 0 rgba(0, 0, 0, 0.16)',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    padding: theme.spacing(4),
    width: '280px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
  title: {
    '& > *': {
      display: 'block',
      fontSize: theme.typography.pxToRem(16),
      lineHeight: 1.5,
      marginBottom: theme.spacing(2),
    },
  },
  author: {
    display: 'inline',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.4,
  },
  journal: {
    fontStyle: 'italic',
  },
}))

interface CitationTooltipProps {
  paperId: string
  citationNumber: number
}

const CitationTooltip: FC<CitationTooltipProps> = ({
  paperId,
  citationNumber,
}): JSX.Element => {
  const { data, error } = usePaperDetailQuery({
    variables: { paperId: paperId || '' },
  })

  const paper = data?.getPapers[0]

  const classes = useStyles()
  const [open, setOpen] = useState(false)

  const onTooltipClose = () => {
    setOpen(false)
  }

  const onTooltipOpen = () => {
    setOpen(true)
  }

  const renderTooltipContent = useMemo(() => {
    if (!paper || error) return null
    return (
      <>
        <div className={classes.title}>
          {paper.pubMedId ? (
            <MUILink>
              <Link {...paperDetailRoute.index(paper.id)} passHref>
                <a target="_blank">{paper.title}</a>
              </Link>
            </MUILink>
          ) : (
            <Typography variant="h3">{paper.title}</Typography>
          )}
        </div>

        <div>
          {paper.authors.map((author, i) => {
            const numberOfAuthors = paper.authors.length - 1
            return (
              <Typography
                variant="body1"
                key={author}
                className={classes.author}
              >
                {author}
                {i === numberOfAuthors ? '' : ', '}
              </Typography>
            )
          })}
        </div>
        <Typography variant="body2" className={classes.journal}>
          {paper.journalName} ({paper.date})
        </Typography>
      </>
    )
  }, [paper])

  return (
    <ClickAwayListener onClickAway={onTooltipClose}>
      <Tooltip
        arrow
        placement="top"
        classes={{
          tooltipArrow: classes.tooltipArrow,
          arrow: classes.arrow,
        }}
        className={classes.citation}
        PopperProps={{
          disablePortal: true,
        }}
        onClose={onTooltipClose}
        open={open}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={renderTooltipContent || ''}
      >
        <a
          onClick={onTooltipOpen}
          onKeyDown={onTooltipOpen}
          role="button"
          tabIndex={0}
        >
          [{citationNumber}]
        </a>
      </Tooltip>
    </ClickAwayListener>
  )
}

export default CitationTooltip
